<script setup lang="tsx">
import { ref, onBeforeMount } from 'vue'
import Multiselect from 'vue-multiselect'
import http from '@/services/http'
import type { Response, RiskCatalogMeta } from '@/models/api-generated'
import type { CatalogFilter } from '@/models/filters-generated'
import { useI18n } from 'vue-i18n'
import { riskConfig } from '../riskConfig'

const props = defineProps<{
	multiSelect: boolean
	allowEmpty: boolean
	class?: string | Record<string, boolean> | Array<string | Record<string, boolean>>
	configId?: number
}>()

const model = defineModel<RiskCatalogMeta | RiskCatalogMeta[]>({ required: true })
const options = ref<RiskCatalogMeta[]>([])
const isLoading = ref(false)

const { t } = useI18n()

onBeforeMount(search)

async function search(q: string = '') {
	isLoading.value = true

	const params: CatalogFilter = {
		q,
		configId: props.configId ?? riskConfig.value.id,
	}

	const { data } = await http.get<Response<RiskCatalogMeta[]>>('/api/risk/catalogs', {
		params,
	})

	if (data.data) options.value = data.data

	isLoading.value = false
}

defineExpose({
	search,
})
</script>

<template>
	<Multiselect
		:class="props.class"
		:allow-empty="props.allowEmpty"
		:multiple="multiSelect"
		:show-labels="false"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="model"
		:custom-label="(option: { catalog: { name: any } }) => option.catalog.name"
		track-by="id"
		@search-change="search"
		:placeholder="t('misc.search')"
		:taggable="false"
	>
		<template #option="{ option }">
			<div class="d-flex justify-content-between align-items-center w-100">
				<span>{{ option.catalog.name }}</span>
			</div>
		</template>
	</Multiselect>
</template>
